import React, { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { startRegistration } from '@simplewebauthn/browser';
import customFetch from '@utils/customFetch';
import { useDashboardContext } from '@pages/DashboardLayout';
import SettingsNav from './SettingsNav';

const mfaSettingsQuery = {
  queryKey: ['mfaSettings'],
  queryFn: async () => {
    const { data } = await customFetch.get('/auth/mfa/settings');
    return data;
  },
};

export const loader = (queryClient) => async () => {
  await queryClient.ensureQueryData(mfaSettingsQuery);
  return null;
};

const inputClasses =
  'block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6';

const primaryButtonClasses =
  'rounded-md bg-svisit-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-500 disabled:cursor-not-allowed disabled:bg-gray-300';

const secondaryButtonClasses =
  'rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400';

const dangerButtonClasses =
  'rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 disabled:cursor-not-allowed disabled:bg-gray-300';

const StatusBadge = ({ enabled }) => (
  <span
    className={
      enabled
        ? 'inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20'
        : 'inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10'
    }
  >
    {enabled ? 'Aktiv' : 'Inaktiv'}
  </span>
);

const Modal = ({ open, onClose, title, children }) => (
  <Dialog open={open} onClose={onClose} className="relative z-50">
    <div className="fixed inset-0 bg-gray-900/50" aria-hidden="true" />
    <div className="fixed inset-0 flex items-center justify-center p-4">
      <DialogPanel className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <DialogTitle className="text-base font-semibold leading-7 text-gray-900">
          {title}
        </DialogTitle>
        <div className="mt-4">{children}</div>
      </DialogPanel>
    </div>
  </Dialog>
);

const PasswordField = ({ password, setPassword }) => (
  <div>
    <label
      htmlFor="reauth-password"
      className="block text-sm font-medium leading-6 text-gray-900"
    >
      Bestätige mit deinem Passwort
    </label>
    <div className="mt-2">
      <input
        id="reauth-password"
        type="password"
        autoComplete="current-password"
        autoFocus
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className={inputClasses}
      />
    </div>
  </div>
);

const RecoveryCodesView = ({ codes, onDone }) => {
  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      toast.success('Codes kopiert');
    } catch (error) {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [`sVisit Recovery-Codes\n\n${codes.join('\n')}\n`],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'svisit-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-700">
        Bewahre diese Recovery-Codes sicher auf. Sie werden nur einmal
        angezeigt und sind dein Zugang, falls du keinen zweiten Faktor mehr
        nutzen kannst. Jeder Code funktioniert nur einmal.
      </p>
      <div className="grid grid-cols-2 gap-2 rounded-md bg-gray-50 p-4 font-mono text-sm text-gray-900">
        {codes.map((code) => (
          <span key={code}>{code}</span>
        ))}
      </div>
      <div className="flex gap-3">
        <button type="button" onClick={copyCodes} className={secondaryButtonClasses}>
          Kopieren
        </button>
        <button type="button" onClick={downloadCodes} className={secondaryButtonClasses}>
          Herunterladen
        </button>
      </div>
      <button type="button" onClick={onDone} className={primaryButtonClasses}>
        Ich habe die Codes gespeichert
      </button>
    </div>
  );
};

const SectionRow = ({ title, description, children }) => (
  <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
    <div>
      <h2 className="text-base font-semibold leading-7 text-gray-900">
        {title}
      </h2>
      <p className="mt-1 text-sm leading-6 text-gray-700">{description}</p>
    </div>
    <div className="md:col-span-2">{children}</div>
  </div>
);

const Security = () => {
  const queryClient = useQueryClient();
  const { data: settings } = useQuery(mfaSettingsQuery);
  const { user } = useDashboardContext();

  const [busy, setBusy] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);

  // TOTP-Setup-Dialog: 'password' → 'qr' → 'recovery'
  const [totpDialog, setTotpDialog] = useState(null);
  const [totpSetupData, setTotpSetupData] = useState(null);
  // E-Mail-OTP-Dialog: 'password' → 'code' → 'recovery'
  const [emailDialog, setEmailDialog] = useState(null);
  // Passkey-Dialog: 'add'
  const [passkeyDialog, setPasskeyDialog] = useState(null);
  const [passkeyToDelete, setPasskeyToDelete] = useState(null);
  const [passkeyToRename, setPasskeyToRename] = useState(null);
  // Disable-Dialoge
  const [disableDialog, setDisableDialog] = useState(null); // 'totp' | 'emailOtp'
  // Recovery-Codes
  const [recoveryDialog, setRecoveryDialog] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [deviceName, setDeviceName] = useState('');

  const refresh = () => queryClient.invalidateQueries(['mfaSettings']);

  const closeAllDialogs = () => {
    setTotpDialog(null);
    setTotpSetupData(null);
    setEmailDialog(null);
    setPasskeyDialog(null);
    setPasskeyToDelete(null);
    setPasskeyToRename(null);
    setDisableDialog(null);
    setRecoveryDialog(false);
    setRecoveryCodes(null);
    setPassword('');
    setCode('');
    setDeviceName('');
  };

  const apiError = (error) =>
    toast.error(error?.response?.data?.msg || 'Etwas ist schiefgelaufen');

  // Passwort ändern läuft sicherheitshalber über den Reset-per-E-Mail-Flow
  const requestPasswordReset = async () => {
    setPasswordResetSent(true);
    try {
      await customFetch.post('/auth/forgot-password', { email: user.email });
      toast.success(
        'Wir haben dir einen Link an deine E-Mail-Adresse gesendet'
      );
    } catch (error) {
      setPasswordResetSent(false);
      toast.error(
        'Es gab ein Problem beim Senden des Links. Bitte versuche es erneut.'
      );
    }
  };

  // --- TOTP ---

  const startTotpSetup = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await customFetch.post('/auth/mfa/totp/setup', {
        password,
      });
      setTotpSetupData(data);
      setTotpDialog('qr');
      setPassword('');
    } catch (error) {
      apiError(error);
    } finally {
      setBusy(false);
    }
  };

  const confirmTotp = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await customFetch.post('/auth/mfa/totp/confirm', {
        code: code.trim(),
      });
      toast.success(data.msg);
      refresh();
      if (data.recoveryCodes) {
        setRecoveryCodes(data.recoveryCodes);
        setTotpDialog('recovery');
      } else {
        closeAllDialogs();
      }
      setCode('');
    } catch (error) {
      apiError(error);
    } finally {
      setBusy(false);
    }
  };

  // --- E-Mail-OTP ---

  const startEmailOtpEnable = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await customFetch.post(
        '/auth/mfa/email-otp/request-enable',
        { password }
      );
      toast.success(data.msg);
      setEmailDialog('code');
      setPassword('');
    } catch (error) {
      apiError(error);
    } finally {
      setBusy(false);
    }
  };

  const confirmEmailOtp = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await customFetch.post('/auth/mfa/email-otp/enable', {
        code: code.trim(),
      });
      toast.success(data.msg);
      refresh();
      if (data.recoveryCodes) {
        setRecoveryCodes(data.recoveryCodes);
        setEmailDialog('recovery');
      } else {
        closeAllDialogs();
      }
      setCode('');
    } catch (error) {
      apiError(error);
    } finally {
      setBusy(false);
    }
  };

  // --- Methoden deaktivieren ---

  const disableMethod = async (event) => {
    event.preventDefault();
    setBusy(true);
    const endpoint =
      disableDialog === 'totp' ? '/auth/mfa/totp' : '/auth/mfa/email-otp';
    try {
      const { data } = await customFetch.delete(endpoint, {
        data: { password },
      });
      toast.success(data.msg);
      refresh();
      closeAllDialogs();
    } catch (error) {
      apiError(error);
    } finally {
      setBusy(false);
    }
  };

  // --- Passkeys ---

  const addPasskey = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await customFetch.post(
        '/auth/mfa/webauthn/register-options',
        { password }
      );
      const attestationResponse = await startRegistration({
        optionsJSON: data.options,
      });
      const { data: registerData } = await customFetch.post(
        '/auth/mfa/webauthn/register',
        { attestationResponse, deviceName: deviceName.trim() }
      );
      toast.success(registerData.msg);
      refresh();
      closeAllDialogs();
    } catch (error) {
      if (error?.name === 'NotAllowedError' || error?.name === 'AbortError') {
        toast.info('Passkey-Registrierung abgebrochen');
      } else if (error?.name === 'InvalidStateError') {
        toast.error('Dieses Gerät ist bereits als Passkey registriert');
      } else {
        apiError(error);
      }
    } finally {
      setBusy(false);
    }
  };

  const renamePasskey = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await customFetch.patch(`/auth/mfa/webauthn/${passkeyToRename.id}`, {
        deviceName: deviceName.trim(),
      });
      toast.success('Passkey umbenannt');
      refresh();
      closeAllDialogs();
    } catch (error) {
      apiError(error);
    } finally {
      setBusy(false);
    }
  };

  const deletePasskey = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await customFetch.delete(
        `/auth/mfa/webauthn/${passkeyToDelete.id}`,
        { data: { password } }
      );
      toast.success(data.msg);
      refresh();
      closeAllDialogs();
    } catch (error) {
      apiError(error);
    } finally {
      setBusy(false);
    }
  };

  // --- Recovery-Codes ---

  const regenerateRecoveryCodes = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await customFetch.post('/auth/mfa/recovery-codes', {
        password,
      });
      setRecoveryCodes(data.recoveryCodes);
      setPassword('');
      refresh();
    } catch (error) {
      apiError(error);
    } finally {
      setBusy(false);
    }
  };

  const hasMfaMethod =
    settings.totpEnabled ||
    settings.emailOtpEnabled ||
    settings.passkeys.length > 0;

  return (
    <main>
      <SettingsNav current="Sicherheit" />

      <div className="divide-y divide-gray/5">
        {/* Passwort */}
        <SectionRow
          title="Passwort"
          description="Ändere das Passwort deines Accounts. Wir senden dir dafür einen Link an deine hinterlegte E-Mail-Adresse."
        >
          <button
            type="button"
            onClick={requestPasswordReset}
            disabled={passwordResetSent}
            className={secondaryButtonClasses}
          >
            {passwordResetSent
              ? 'Link gesendet — bitte prüfe dein Postfach'
              : 'Link zum Ändern des Passworts anfordern'}
          </button>
        </SectionRow>

        {/* TOTP */}
        <SectionRow
          title="Authenticator-App (TOTP)"
          description="Nutze eine Authenticator-App wie Google Authenticator, Aegis oder 1Password als zweiten Faktor bei der Anmeldung."
        >
          <div className="flex items-center gap-x-4">
            <StatusBadge enabled={settings.totpEnabled} />
            {settings.totpEnabled ? (
              <button
                type="button"
                onClick={() => setDisableDialog('totp')}
                className={dangerButtonClasses}
              >
                Deaktivieren
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setTotpDialog('password')}
                className={primaryButtonClasses}
              >
                Einrichten
              </button>
            )}
          </div>
        </SectionRow>

        {/* E-Mail-OTP */}
        <SectionRow
          title="E-Mail-Code"
          description="Erhalte bei jeder Anmeldung einen 6-stelligen Bestätigungscode per E-Mail."
        >
          <div className="flex items-center gap-x-4">
            <StatusBadge enabled={settings.emailOtpEnabled} />
            {settings.emailOtpEnabled ? (
              <button
                type="button"
                onClick={() => setDisableDialog('emailOtp')}
                className={dangerButtonClasses}
              >
                Deaktivieren
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setEmailDialog('password')}
                className={primaryButtonClasses}
              >
                Aktivieren
              </button>
            )}
          </div>
        </SectionRow>

        {/* Passkeys */}
        <SectionRow
          title="Passkeys"
          description="Melde dich passwortlos mit Touch ID, Windows Hello oder einem Hardware-Key an. Passkeys dienen gleichzeitig als zweiter Faktor."
        >
          <div className="space-y-4 sm:max-w-xl">
            {settings.passkeys.length === 0 ? (
              <p className="text-sm text-gray-700">
                Noch keine Passkeys hinterlegt.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-md ring-1 ring-gray-200">
                {settings.passkeys.map((passkey) => (
                  <li
                    key={passkey.id}
                    className="flex items-center justify-between gap-x-4 px-4 py-3"
                  >
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {passkey.deviceName}
                      </p>
                      <p className="text-xs text-gray-500">
                        Erstellt am{' '}
                        {dayjs(passkey.createdAt).format('DD.MM.YYYY')}
                        {passkey.lastUsedAt &&
                          ` · Zuletzt verwendet am ${dayjs(
                            passkey.lastUsedAt
                          ).format('DD.MM.YYYY')}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setPasskeyToRename(passkey);
                          setDeviceName(passkey.deviceName);
                        }}
                        className="text-sm font-semibold text-svisit-primary-600 hover:text-svisit-primary-500"
                      >
                        Umbenennen
                      </button>
                      <button
                        type="button"
                        onClick={() => setPasskeyToDelete(passkey)}
                        className="text-sm font-semibold text-red-600 hover:text-red-500"
                      >
                        Entfernen
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => setPasskeyDialog('add')}
              className={primaryButtonClasses}
            >
              Passkey hinzufügen
            </button>
          </div>
        </SectionRow>

        {/* Recovery-Codes */}
        <SectionRow
          title="Recovery-Codes"
          description="Einmal-Codes als Fallback, falls du keinen Zugriff mehr auf deinen zweiten Faktor hast."
        >
          <div className="flex items-center gap-x-4">
            <p className="text-sm text-gray-700">
              {settings.recoveryCodesRemaining} unbenutzte Codes
            </p>
            <button
              type="button"
              onClick={() => setRecoveryDialog(true)}
              disabled={!hasMfaMethod}
              className={secondaryButtonClasses}
            >
              Neu generieren
            </button>
          </div>
          {!hasMfaMethod && (
            <p className="mt-2 text-xs text-gray-500">
              Recovery-Codes sind erst mit einer aktiven MFA-Methode verfügbar.
            </p>
          )}
        </SectionRow>
      </div>

      {/* TOTP-Setup-Dialog */}
      <Modal
        open={!!totpDialog}
        onClose={totpDialog === 'recovery' ? () => {} : closeAllDialogs}
        title={
          totpDialog === 'recovery'
            ? 'Recovery-Codes'
            : 'Authenticator-App einrichten'
        }
      >
        {totpDialog === 'password' && (
          <form onSubmit={startTotpSetup} className="space-y-4">
            <PasswordField password={password} setPassword={setPassword} />
            <button type="submit" className={primaryButtonClasses} disabled={busy}>
              {busy ? 'Lädt...' : 'Weiter'}
            </button>
          </form>
        )}
        {totpDialog === 'qr' && totpSetupData && (
          <form onSubmit={confirmTotp} className="space-y-4">
            <p className="text-sm text-gray-700">
              Scanne den QR-Code mit deiner Authenticator-App und gib danach
              den angezeigten Code ein.
            </p>
            <div className="flex justify-center">
              <img
                src={totpSetupData.qrCode}
                alt="TOTP QR-Code"
                className="h-48 w-48"
              />
            </div>
            <p className="break-all text-center font-mono text-xs text-gray-500">
              {totpSetupData.secret}
            </p>
            <div>
              <label
                htmlFor="totp-code"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Code aus der App
              </label>
              <div className="mt-2">
                <input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  className={inputClasses}
                />
              </div>
            </div>
            <button type="submit" className={primaryButtonClasses} disabled={busy}>
              {busy ? 'Prüft...' : 'Aktivieren'}
            </button>
          </form>
        )}
        {totpDialog === 'recovery' && recoveryCodes && (
          <RecoveryCodesView codes={recoveryCodes} onDone={closeAllDialogs} />
        )}
      </Modal>

      {/* E-Mail-OTP-Dialog */}
      <Modal
        open={!!emailDialog}
        onClose={emailDialog === 'recovery' ? () => {} : closeAllDialogs}
        title={
          emailDialog === 'recovery'
            ? 'Recovery-Codes'
            : 'E-Mail-Code aktivieren'
        }
      >
        {emailDialog === 'password' && (
          <form onSubmit={startEmailOtpEnable} className="space-y-4">
            <p className="text-sm text-gray-700">
              Wir senden dir zur Bestätigung einen Code an deine E-Mail-Adresse.
            </p>
            <PasswordField password={password} setPassword={setPassword} />
            <button type="submit" className={primaryButtonClasses} disabled={busy}>
              {busy ? 'Sendet...' : 'Code senden'}
            </button>
          </form>
        )}
        {emailDialog === 'code' && (
          <form onSubmit={confirmEmailOtp} className="space-y-4">
            <div>
              <label
                htmlFor="email-otp-code"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Code aus der E-Mail
              </label>
              <div className="mt-2">
                <input
                  id="email-otp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  className={inputClasses}
                />
              </div>
            </div>
            <button type="submit" className={primaryButtonClasses} disabled={busy}>
              {busy ? 'Prüft...' : 'Aktivieren'}
            </button>
          </form>
        )}
        {emailDialog === 'recovery' && recoveryCodes && (
          <RecoveryCodesView codes={recoveryCodes} onDone={closeAllDialogs} />
        )}
      </Modal>

      {/* Methode deaktivieren */}
      <Modal
        open={!!disableDialog}
        onClose={closeAllDialogs}
        title={
          disableDialog === 'totp'
            ? 'Authenticator-App deaktivieren'
            : 'E-Mail-Code deaktivieren'
        }
      >
        <form onSubmit={disableMethod} className="space-y-4">
          <p className="text-sm text-gray-700">
            Dein Account ist danach weniger gut geschützt.
          </p>
          <PasswordField password={password} setPassword={setPassword} />
          <button type="submit" className={dangerButtonClasses} disabled={busy}>
            {busy ? 'Deaktiviert...' : 'Deaktivieren'}
          </button>
        </form>
      </Modal>

      {/* Passkey hinzufügen */}
      <Modal
        open={passkeyDialog === 'add'}
        onClose={closeAllDialogs}
        title="Passkey hinzufügen"
      >
        <form onSubmit={addPasskey} className="space-y-4">
          <div>
            <label
              htmlFor="device-name"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Name des Passkeys (z.&nbsp;B. «MacBook», «iPhone»)
            </label>
            <div className="mt-2">
              <input
                id="device-name"
                type="text"
                maxLength={50}
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="Mein Gerät"
                className={inputClasses}
              />
            </div>
          </div>
          <PasswordField password={password} setPassword={setPassword} />
          <button type="submit" className={primaryButtonClasses} disabled={busy}>
            {busy ? 'Wartet auf Gerät...' : 'Passkey erstellen'}
          </button>
        </form>
      </Modal>

      {/* Passkey umbenennen */}
      <Modal
        open={!!passkeyToRename}
        onClose={closeAllDialogs}
        title="Passkey umbenennen"
      >
        <form onSubmit={renamePasskey} className="space-y-4">
          <div>
            <label
              htmlFor="rename-device-name"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Neuer Name
            </label>
            <div className="mt-2">
              <input
                id="rename-device-name"
                type="text"
                maxLength={50}
                required
                autoFocus
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                className={inputClasses}
              />
            </div>
          </div>
          <button type="submit" className={primaryButtonClasses} disabled={busy}>
            {busy ? 'Speichert...' : 'Speichern'}
          </button>
        </form>
      </Modal>

      {/* Passkey entfernen */}
      <Modal
        open={!!passkeyToDelete}
        onClose={closeAllDialogs}
        title={
          passkeyToDelete
            ? `Passkey «${passkeyToDelete.deviceName}» entfernen`
            : ''
        }
      >
        <form onSubmit={deletePasskey} className="space-y-4">
          <p className="text-sm text-gray-700">
            Du kannst dich danach nicht mehr mit diesem Passkey anmelden.
          </p>
          <PasswordField password={password} setPassword={setPassword} />
          <button type="submit" className={dangerButtonClasses} disabled={busy}>
            {busy ? 'Entfernt...' : 'Entfernen'}
          </button>
        </form>
      </Modal>

      {/* Recovery-Codes neu generieren */}
      <Modal
        open={recoveryDialog}
        onClose={recoveryCodes ? () => {} : closeAllDialogs}
        title="Recovery-Codes neu generieren"
      >
        {recoveryCodes ? (
          <RecoveryCodesView codes={recoveryCodes} onDone={closeAllDialogs} />
        ) : (
          <form onSubmit={regenerateRecoveryCodes} className="space-y-4">
            <p className="text-sm text-gray-700">
              Alle bisherigen Recovery-Codes werden dadurch ungültig.
            </p>
            <PasswordField password={password} setPassword={setPassword} />
            <button type="submit" className={primaryButtonClasses} disabled={busy}>
              {busy ? 'Generiert...' : 'Neu generieren'}
            </button>
          </form>
        )}
      </Modal>
    </main>
  );
};

export default Security;
